import axios, { AxiosResponse } from 'axios';
import { configHelper as ch } from '@ilteducation/config';
import {
  GetOrganizationUsersResponse,
  GetOrganizationUsersRequest,
  GetGroupsRequest,
  GetUserResponse,
  GetUserRequest,
  UpdateUserRequest,
  GetMembershipsRequest,
  GetMembershipsResponse,
  GetAssociationsRequest,
  GetAssociationsResponse,
  CreateUserRequest,
  GetGroupsRequestFilters,
  GetGroupsResponse,
  GetGroupRequest,
  GetGroupResponse,
  UpdateGroupRequest,
  UpdateAssociationRequest,
  CreateUserResponse,
  SearchUsersRequest,
  SearchUsersResponse,
  CreateGroupRequest,
  CreateGroupResponse,
  UpdateGroupResponse,
  UsersExistsResponse,
  UsersExistsRequest,
  GetOrganizationRequest,
  GetOrganizationResponse,
  GetOrganizationsRequest,
  GetOrganizationsResponse,
  GetUsersRequest,
  GetUsersResponse,
  CreateAssociationsRequest,
  DeleteAssociationResponse,
  GetOrganizationsSubscriptionPeriodsResponse,
  GetUsersSubscriptionPeriodsRequest,
  GetUsersSubscriptionPeriodsResponse,
  GetOrganizationsSubscriptionPeriodsForUserRequest,
  GetOrganizationsSubscriptionPeriodsRequest,
  CreateUserSubscriptionPeriodRequest,
  CreateUserSubscriptionPeriodResponse,
  UpdateUserSubscriptionPeriodRequest,
  DeleteUserSubscriptionPeriodRequest,
  CreateOrganizationSubscriptionPeriodRequest,
  CreateOrganizationSubscriptionPeriodResponse,
  UpdateOrganizationSubscriptionPeriodRequest,
  DeleteOrganizationSubscriptionPeriodRequest,
  GetSubscriptionAuditLogsResponse,
  GetSubscriptionPeriodAuditLogsRequest,
  GetTransactionalEmailAuditLogsRequest,
  GetTransactionalEmailAuditLogsResponse,
} from '@ilteducation/customer-mgmt-types';
import useQuery from './composables/useQuery';
import type { FetchListOptions } from './types/types';

export const customerMgmt = ch.getApiUrl('customer-mgmt');
const { makeQuery, makeQueryByObject } = useQuery();

export default {
  fetchers: {
    fetchSchoolUnits(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/schoolUnits?${makeQuery(options)}`);
    },

    fetchUserSchoolUnits(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/userSchoolUnits?${makeQuery(options)}`);
    },

    fetchGroups(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/groups?${makeQuery(options)}`);
    },

    fetchUsers(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/users?${makeQuery(options)}`);
    },
  },
  getters: {
    getUsers(userUid: string) {
      return axios.get(`${customerMgmt}/users/${userUid}`);
    },
    getSchoolUnits(schoolUnitUid: string) {
      return axios.get(`${customerMgmt}/schoolUnits/${schoolUnitUid}`);
    },
  },

  associations: {
    get(options: GetAssociationsRequest): Promise<AxiosResponse<GetAssociationsResponse>> {
      return axios.get(`${customerMgmt}/userSchoolUnits?${makeQueryByObject(options)}`);
    },
  },
  memberships: {
    get(options: GetMembershipsRequest): Promise<AxiosResponse<GetMembershipsResponse>> {
      return axios.get(`${customerMgmt}/userGroups?${makeQueryByObject(options)}`);
    },
  },
  organizations: {
    get(data: GetOrganizationsRequest = {}): Promise<AxiosResponse<GetOrganizationsResponse>> {
      return axios.get(`${customerMgmt}/schoolUnits?${makeQueryByObject(data)}`);
    },
  },
  users: {
    get(data: GetUsersRequest = {}): Promise<AxiosResponse<GetUsersResponse>> {
      return axios.get(`${customerMgmt}/users?${makeQueryByObject(data)}`);
    },
  },
  user: {
    get(uid: GetUserRequest['userId']): Promise<AxiosResponse<GetUserResponse>> {
      return axios.get(`${customerMgmt}/users/${uid}`);
    },
    create(data: CreateUserRequest): Promise<AxiosResponse<CreateUserResponse>> {
      return axios.post(`${customerMgmt}/users`, data);
    },
    patch(uid: UpdateUserRequest['userId'], data: Omit<UpdateUserRequest, 'userId'>): Promise<AxiosResponse<void>> {
      return axios.patch(`${customerMgmt}/users/${uid}`, data);
    },
    upsertAssociations(
      userUid: string,
      schoolAssociations: CreateAssociationsRequest['schoolAssociations'],
    ): Promise<AxiosResponse<void>> {
      return axios.put(`${customerMgmt}/users/${userUid}/schoolUnits`, { schoolAssociations });
    },
    updateAssociation(
      userUid: string,
      schoolUnitId: string,
      role: UpdateAssociationRequest['role'],
    ): Promise<AxiosResponse<void>> {
      return axios.put(`${customerMgmt}/users/${userUid}/schoolUnits/${schoolUnitId}`, { role });
    },
    deleteAssociation(userUid: string, schoolUnitId: string): Promise<AxiosResponse<DeleteAssociationResponse>> {
      return axios.delete(`${customerMgmt}/users/${userUid}/schoolUnits/${schoolUnitId}`);
    },
    deleteMembership(userUid: string, groupUid: string) {
      return axios.delete(`${customerMgmt}/users/${userUid}/groups/${groupUid}`);
    },
    search: (options: SearchUsersRequest): Promise<AxiosResponse<SearchUsersResponse>> => {
      return axios.post(`${customerMgmt}/users/search`, options);
    },
    exists: (body: UsersExistsRequest): Promise<AxiosResponse<UsersExistsResponse>> => {
      return axios.post(`${customerMgmt}/users/exists`, body);
    },
    getOrganizationSubscriptionPeriods: (
      options: GetOrganizationsSubscriptionPeriodsForUserRequest,
    ): Promise<AxiosResponse<GetOrganizationsSubscriptionPeriodsResponse>> => {
      return axios.get(
        `${customerMgmt}/users/${options.userUid}/organizationSubscriptions?${makeQueryByObject(options)}`,
      );
    },
    getUserSubscriptionPeriods: (
      options: GetUsersSubscriptionPeriodsRequest,
    ): Promise<AxiosResponse<GetUsersSubscriptionPeriodsResponse>> => {
      return axios.get(`${customerMgmt}/userSubscriptionPeriods?${makeQueryByObject(options)}`);
    },
    createUserSubscriptionPeriod: (
      data: CreateUserSubscriptionPeriodRequest,
    ): Promise<AxiosResponse<CreateUserSubscriptionPeriodResponse>> => {
      return axios.post(`${customerMgmt}/userSubscriptionPeriods`, data);
    },
    updateUserSubscriptionPeriod: (data: UpdateUserSubscriptionPeriodRequest): Promise<AxiosResponse<void>> => {
      return axios.put(`${customerMgmt}/userSubscriptionPeriods/${data.periodUid}`, data);
    },
    deleteUserSubscriptionPeriod: (data: DeleteUserSubscriptionPeriodRequest): Promise<AxiosResponse<void>> => {
      return axios.delete(`${customerMgmt}/userSubscriptionPeriods/${data.userUid}/${data.periodUid}`);
    },
  },
  organization: {
    get: (
      organizationId: Required<GetOrganizationRequest['organizationId']>,
    ): Promise<AxiosResponse<GetOrganizationResponse>> => {
      return axios.get(`${customerMgmt}/schoolUnits/${organizationId}`);
    },

    /**
     * This user list faster then regular users endpoint since permissions are check for only one specific organization.
     */
    users: (
      organizationUid: GetOrganizationUsersRequest['organizationUid'],
      options: Omit<GetOrganizationUsersRequest, 'organizationUid'>,
    ): Promise<AxiosResponse<GetOrganizationUsersResponse>> => {
      return axios.get(`${customerMgmt}/organizations/${organizationUid}/users?${makeQueryByObject(options)}`, {});
    },

    /**
     * @TODO: Implement this endpoint
     * @param organizationUid
     * @param options
     * @returns
     */
    groups: (
      organizationUid: Required<GetGroupsRequestFilters>['schoolUnits'][number],
      options: Omit<GetGroupsRequest, 'schoolUnitId'>,
    ): Promise<AxiosResponse<GetGroupsResponse>> => {
      return axios.get(
        `${customerMgmt}/groups?${makeQueryByObject({
          ...options,
          filters: {
            ...(options.filters ?? {}),
            schoolUnits: [organizationUid],
          },
        } satisfies GetGroupsRequest)}`,
      );
    },
    subscriptionPeriods: (
      options: GetOrganizationsSubscriptionPeriodsRequest,
    ): Promise<AxiosResponse<GetOrganizationsSubscriptionPeriodsResponse>> => {
      return axios.get(`${customerMgmt}/organizationSubscriptionPeriods?${makeQueryByObject(options)}`);
    },
    createSubscriptionPeriod: (
      data: CreateOrganizationSubscriptionPeriodRequest,
    ): Promise<AxiosResponse<CreateOrganizationSubscriptionPeriodResponse>> => {
      return axios.post(`${customerMgmt}/organizationSubscriptionPeriods`, data);
    },
    updateSubscriptionPeriod: (data: UpdateOrganizationSubscriptionPeriodRequest): Promise<AxiosResponse<void>> => {
      return axios.put(`${customerMgmt}/organizationSubscriptionPeriods/${data.periodUid}`, data);
    },
    deleteSubscriptionPeriod: (data: DeleteOrganizationSubscriptionPeriodRequest): Promise<AxiosResponse<void>> => {
      return axios.delete(`${customerMgmt}/organizationSubscriptionPeriods/${data.organizationUid}/${data.periodUid}`);
    },
  },
  group: {
    get: (groupId: Required<GetGroupRequest>['groupId']): Promise<AxiosResponse<GetGroupResponse>> => {
      return axios.get(`${customerMgmt}/groups/${groupId}`);
    },
    create: (data: CreateGroupRequest): Promise<AxiosResponse<CreateGroupResponse>> => {
      return axios.put(`${customerMgmt}/groups`, data);
    },
    patch(groupUid: string, data: Omit<UpdateGroupRequest, 'groupUid'>): Promise<AxiosResponse<UpdateGroupResponse>> {
      return axios.patch(`${customerMgmt}/groups/${groupUid}`, data);
    },
    delete(groupUid: string): Promise<AxiosResponse<void>> {
      return axios.delete(`${customerMgmt}/groups/${groupUid}`);
    },
    addMember: (groupUid: string, userUid: string): Promise<AxiosResponse<void>> => {
      return axios.put(`${customerMgmt}/users/${userUid}/groups/${groupUid}`);
    },
    removeMember: (groupUid: string, userUid: string): Promise<AxiosResponse<void>> => {
      return axios.delete(`${customerMgmt}/users/${userUid}/groups/${groupUid}`);
    },
    users: (
      groupId: string,
      options: Omit<SearchUsersRequest, 'filters'>,
    ): Promise<AxiosResponse<SearchUsersResponse>> => {
      const requestOptions = {
        ...options,
        filters: {
          organizations: { groups: { uids: [groupId] } },
        },
      } satisfies SearchUsersRequest;
      return axios.post(`${customerMgmt}/users/search`, requestOptions);
    },
  },
  subscription: {
    getAudits: (
      ownerUid: string,
      options: Omit<GetSubscriptionPeriodAuditLogsRequest, 'ownerUid'>,
    ): Promise<AxiosResponse<GetSubscriptionAuditLogsResponse>> => {
      return axios.get(`${customerMgmt}/subscriptions/${ownerUid}/audits?${makeQueryByObject(options)}`);
    },
  },
  transactionalEmail: {
    getAuditLogs: ({
      userUid,
      pageNumber,
      pageSize,
    }: GetTransactionalEmailAuditLogsRequest): Promise<AxiosResponse<GetTransactionalEmailAuditLogsResponse>> => {
      return axios.get(
        `${customerMgmt}/users/${userUid}/transactional-email/auditLogs?${makeQueryByObject({ pageNumber, pageSize })}`,
      );
    },
  },
  import: {
    upsertUser(data: CreateUserRequest): Promise<AxiosResponse<CreateUserResponse>> {
      return axios.post(`${customerMgmt}/import/upsert-user`, data);
    },
  },
};
